import React, { forwardRef } from "react";
import { AnimatePresence, motion } from "framer-motion";

import useId from "@kikoff/hooks/src/useId";
import { cls } from "@kikoff/utils/src/string";

import { CheckPathAnimation } from "@component/animations/svg_animations";

import styles from "./checkbox.module.scss";

interface CheckboxPathProps extends React.HTMLProps<HTMLInputElement> {}

export const CheckboxPath = forwardRef<HTMLDivElement, CheckboxPathProps>(
  (props, ref) => {
    return (
      <div className={styles["checkbox-path"]} ref={ref}>
        <input type="checkbox" {...props} />
        <svg viewBox="0 0 21 21">
          <path d="M5,10.75 L8.5,14.25 L19.4,2.3 C18.8333333,1.43333333 18.0333333,1 17,1 L4,1 C2.35,1 1,2.35 1,4 L1,17 C1,18.65 2.35,20 4,20 L17,20 C18.65,20 20,18.65 20,17 L20,7.99769186" />
        </svg>
      </div>
    );
  }
);

interface LabeledCheckboxProps extends React.HTMLProps<HTMLInputElement> {
  label: string;
}

export const LabeledCheckbox: React.FC<LabeledCheckboxProps> = ({
  label,
  ...props
}) => {
  const id = useId();

  return (
    <div className={styles["labeled-checkbox"]}>
      <input type="checkbox" {...props} id={id} />
      <label htmlFor={id}>
        <div className={styles["check-container"]}>
          <CheckPathAnimation className={styles.check} duration={0.5} />
        </div>
        <div className={styles.content}>{label}</div>
      </label>
    </div>
  );
};

export interface KCheckboxProps extends React.HTMLProps<HTMLInputElement> {
  inline?: boolean;
  valid?: boolean;
  error?: Error;
  propsFor?: {
    checkbox?: React.ComponentProps<"div">;
  };
}

export function KCheckbox({
  inline,
  className,
  children,
  valid = true,
  error,
  propsFor = {},
  ...props
}: KCheckboxProps) {
  const id = useId(props.id);

  return (
    <div
      className={cls(
        styles["k-checkbox-container"],
        inline && styles.inline,
        className
      )}
    >
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: null, opacity: 1 }}
            exit={{ height: 0, opacity: -1 }}
          >
            <div className="text:mini+ color:error pb-1">{error.message}</div>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        {...propsFor.checkbox}
        className={cls(styles["k-checkbox"], propsFor.checkbox?.className)}
      >
        <input type="checkbox" {...props} id={id} />
        <label className={styles.checkbox} htmlFor={id} data-cy="k-checkbox">
          <svg
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              className={styles.box}
              x="0.75"
              y="0.75"
              width="23.5"
              height="23.5"
              rx="1.25"
              fill="#00c434"
              stroke={valid ? "#1D1D1D" : "var(--error)"}
              strokeWidth="1.5"
            />
            <path
              className={styles.check}
              d="M11.5662 18.7571L20.7651 9.50463C20.8396 9.42996 20.8987 9.34124 20.939 9.24357C20.9793 9.14589 21 9.04117 21 8.93542C21 8.82967 20.9793 8.72495 20.939 8.62728C20.8987 8.5296 20.8396 8.44088 20.7651 8.36621L19.6333 7.2361C19.5592 7.16126 19.4711 7.10189 19.3741 7.06137C19.2771 7.02085 19.1732 7 19.0682 7C18.9631 7 18.8592 7.02085 18.7622 7.06137C18.6652 7.10189 18.5771 7.16126 18.503 7.2361L11.0003 14.7826L7.49718 11.2507C7.3468 11.1003 7.14334 11.0159 6.93125 11.0159C6.71917 11.0159 6.51572 11.1003 6.36534 11.2507L5.23349 12.3958C5.08394 12.5471 5 12.7517 5 12.9651C5 13.1784 5.08394 13.383 5.23349 13.5343L10.4346 18.7638C10.5089 18.8387 10.5971 18.8981 10.6942 18.9386C10.7913 18.9791 10.8954 19 11.0005 19C11.1057 19 11.2098 18.9791 11.3069 18.9386C11.404 18.8981 11.4922 18.8387 11.5665 18.7638L11.5662 18.7571Z"
              fill="#ffffff"
            />
          </svg>
        </label>
        {children && (
          <label
            className={styles.label}
            htmlFor={id}
            data-cy="k-checkbox-extended"
          >
            {children}
          </label>
        )}
      </div>
    </div>
  );
}

KCheckbox.List = List;

declare namespace KCheckbox {
  namespace List {
    type Props = {
      checkedList: boolean[];
      contentList: React.ReactNode[];
      onChange: (i: number, e: React.FormEvent<HTMLInputElement>) => void;
    };
  }
}

function List({ checkedList, contentList, onChange }: KCheckbox.List.Props) {
  return (
    <>
      {contentList.map((content, i) => (
        <KCheckbox
          className="pb-2"
          checked={checkedList[i]}
          onChange={(e) => onChange(i, e)}
        >
          {content}
        </KCheckbox>
      ))}
    </>
  );
}

KCheckbox.Map = Map;

declare namespace KCheckbox {
  namespace Map {
    type Props<Key extends string> = {
      checkedMap: Record<Key, boolean>;
      contentMap: Record<Key, React.ReactNode>;
      onChange: (key: Key, e: React.FormEvent<HTMLInputElement>) => void;
    };
  }
}

function Map<Key extends string>({
  checkedMap,
  contentMap,
  onChange,
}: KCheckbox.Map.Props<Key>) {
  return (
    <>
      {Object.entries(checkedMap).map(([key, checked]) => (
        <KCheckbox
          className="pb-2"
          checked={checked as boolean}
          onChange={(e) => onChange(key as Key, e)}
        >
          {contentMap[key]}
        </KCheckbox>
      ))}
    </>
  );
}
